import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
    Box, Button, Card, CircularProgress, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Typography,
} from '@material-ui/core';

import { listSurveys, fmtDateTime } from '../../services/surveys.service';

/**
 * Surveys — the public student / teacher AI & digital learning surveys.
 *
 * Each survey's wording is edited here and published as a new version, without a code
 * change or a deploy (Edit text). Responses are browsed and exported per survey.
 *
 * Material-UI v4, so styling is `style=` rather than `sx=`.
 */

const Pill = ({ children, fg, bg }) => (
    <span style={{
        display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 12,
        fontWeight: 700, color: fg, background: bg, whiteSpace: 'nowrap',
    }}>
        {children}
    </span>
);

const SurveysList = () => {
    const history = useHistory();
    const [surveys, setSurveys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        let live = true;
        listSurveys()
            .then((res) => { if (live) setSurveys((res && res.data && res.data.surveys) || []); })
            .catch((e) => { if (live) setError((e && e.message) || 'Could not load surveys.'); })
            .finally(() => { if (live) setLoading(false); });
        return () => { live = false; };
    }, []);

    return (
        <Box style={{ padding: 24 }}>
            <Typography variant="h5" style={{ fontWeight: 700, marginBottom: 4 }}>Surveys</Typography>
            <Typography variant="body2" style={{ color: '#666', marginBottom: 20 }}>
                The public AI &amp; digital learning surveys. Edit their wording and publish it
                as a new version, then read and export the responses. Questions, options and
                rules are set in code; only the text can be changed here.
            </Typography>

            <Card style={{ padding: 16 }}>
                {loading && <Box style={{ padding: 32, textAlign: 'center' }}><CircularProgress size={28} /></Box>}
                {!loading && error && <Typography color="error">{error}</Typography>}
                {!loading && !error && (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Survey</TableCell>
                                    <TableCell>Audience</TableCell>
                                    <TableCell>Live version</TableCell>
                                    <TableCell>Draft</TableCell>
                                    <TableCell align="right">Responses</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {surveys.length === 0 && (
                                    <TableRow><TableCell colSpan={6} style={{ color: '#888' }}>No surveys yet.</TableCell></TableRow>
                                )}
                                {surveys.map((s) => (
                                    <TableRow key={s.survey_code} hover>
                                        <TableCell>
                                            <div style={{ fontWeight: 600 }}>{s.title}</div>
                                            <div style={{ fontSize: 12, color: '#888' }}>{s.survey_code}</div>
                                        </TableCell>
                                        <TableCell style={{ textTransform: 'capitalize' }}>{s.audience || '—'}</TableCell>
                                        <TableCell>
                                            {s.live_version
                                                ? <><Pill fg="#1b6e3a" bg="#e6f4ea">v{s.live_version}</Pill>
                                                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>since {fmtDateTime(s.live_since)}</div></>
                                                : '—'}
                                        </TableCell>
                                        <TableCell>
                                            {s.draft
                                                ? <><Pill fg="#8a5300" bg="#fff3e0">Draft v{s.draft.version}</Pill>
                                                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                                                        {s.draft.updated_by ? `${s.draft.updated_by}, ` : ''}{fmtDateTime(s.draft.updated_at)}
                                                    </div></>
                                                : <span style={{ color: '#aaa' }}>—</span>}
                                        </TableCell>
                                        <TableCell align="right" style={{ fontWeight: 600 }}>{s.responses}</TableCell>
                                        <TableCell align="right" style={{ whiteSpace: 'nowrap' }}>
                                            <Button size="small" variant="outlined" color="primary" style={{ marginRight: 8 }}
                                                onClick={() => history.push(`/surveys/edit/${s.survey_code}`)}>
                                                Edit text
                                            </Button>
                                            <Button size="small" variant="outlined"
                                                onClick={() => history.push(`/surveys/responses/${s.survey_code}`)}>
                                                Responses
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Card>
        </Box>
    );
};

export default SurveysList;
